import { useEffect, useState } from 'react'
import { BuildingBeamSize } from '../../../../models/Construction'
import {
  Box,
  Button,
  Collapse,
  Divider,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { ListShowMoreIcon } from '../../../Svgs'
import { useDispatch, useSelector } from 'react-redux'
import {
  createStructuralElement,
  setSelectedToolProps,
} from '../../../../actions/construction'
import store, { RootState } from '../../../../store'
import { SceneManager } from '../../../../visualization/sceneManager'
import { EventType } from '../../../../utils/EventEmitter'
import { createSelector } from '@reduxjs/toolkit'
import { updateHistory } from '../../../../actions/history'
import { ActionType } from '../../../../reducers/history'
import { getEnums } from '../../../../utils/Common'
import {
  RectangleFramingElement,
  RectangleFramingElementType,
} from '../../../../models/Structural'

const convertDataSelector = createSelector(
  [(state: RootState) => state.construction],
  (construction) => construction.convertData,
)

const CreateStructuralElement = () => {
  const dispatch = useDispatch()
  const [isOpen, setIsOpen] = useState(true)
  const beamSize = useSelector(
    (state: RootState) => state.construction.selectedToolProps.beamSize,
  )

  useEffect(() => {
    const eventEmitter = SceneManager.getInstance().getEventEmitter()
    if (!eventEmitter) {
      return
    }

    const handleNewStructuralElementCreation =
      (createStructuralElementPayload: {
        element: RectangleFramingElement
        elementType: RectangleFramingElementType
        storeyId: string
      }) => {
        dispatch(createStructuralElement(createStructuralElementPayload))

        const convertData = structuredClone(
          convertDataSelector(store.getState()),
        )
        dispatch(
          updateHistory({
            convertData,
            items: [
              {
                itemId: createStructuralElementPayload.element.id,
                actionType: ActionType.ADD,
              },
            ],
          }),
        )
      }

    eventEmitter.on(
      EventType.BuildingAddStructuralElement,
      handleNewStructuralElementCreation,
    )

    return () => {
      eventEmitter.off(
        EventType.BuildingAddStructuralElement,
        handleNewStructuralElementCreation,
      )
    }
  }, [])

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          paddingLeft: '12px',
          paddingRight: '4px',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          color='#04002E'
          sx={{
            fontSize: '13px',
            fontWeight: 500,
            lineHeight: 'normal',
          }}
        >
          Create a New Beam Settings
        </Typography>

        <IconButton
          onClick={() => setIsOpen((prev) => !prev)}
          sx={{
            transition: 'transform 0.3s',
            transform: isOpen ? 'rotate(90deg)' : 'rotate(180deg)',
          }}
        >
          <ListShowMoreIcon
            sx={{
              fontSize: '14px',
              color: '#797979',
            }}
          />
        </IconButton>
      </Box>

      <Divider
        flexItem
        orientation='horizontal'
        sx={{
          backgroundColor: '#E4E4E4',
        }}
      />

      <Collapse in={isOpen} timeout='auto' unmountOnExit>
        <Stack
          direction='column'
          sx={{
            padding: '8px 12px',
            gap: '16px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
            }}
          >
            <Typography
              sx={{
                color: '#737373',

                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: 'normal',
                textAlign: 'left',
              }}
            >
              Beam Size
            </Typography>

            <ToggleButtonGroup
              size='small'
              color='primary'
              value={beamSize}
              exclusive
              aria-label='Platform'
              sx={{
                flex: 1,
                borderRadius: '4px',
                backgroundColor: '#F2F2F2',
                '.MuiToggleButton-root': {
                  margin: 0,
                  border: 'none',
                  borderRadius: '4px',
                },
              }}
            >
              {getEnums(BuildingBeamSize).map(([key, value]) => (
                <ToggleButton
                  key={key}
                  value={value}
                  onClick={() =>
                    dispatch(
                      setSelectedToolProps({
                        beamSize: value,
                      }),
                    )
                  }
                  sx={{
                    width: '100%',
                    backgroundColor: '#F5F5F5',
                    '&:hover': {
                      backgroundColor: '#E5F0FF',
                    },
                    '&.Mui-selected': {
                      backgroundColor: '#1705E5',
                      '&:hover': {
                        backgroundColor: '#1705E5',
                      },
                      '.MuiTypography-root': {
                        color: 'white',
                      },
                    },
                  }}
                >
                  <Typography
                    sx={{
                      textAlign: 'center',

                      fontSize: '12px',
                      fontStyle: 'normal',
                      fontWeight: 500,
                      lineHeight: 'normal',
                      textTransform: 'lowercase',
                      color: '#97999B',
                    }}
                  >
                    {value.toString()}
                  </Typography>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <Button
              disableRipple
              sx={{
                color: '#fff',
                background: '#1705E5',
                textTransform: 'none',
                fontSize: '12px',
                fontWeight: 500,
                lineHeight: 'normal',
              }}
            >
              Add Beam
            </Button>
          </Box>
        </Stack>

        <Divider
          flexItem
          orientation='horizontal'
          sx={{
            backgroundColor: '#E4E4E4',
          }}
        />
      </Collapse>
    </Box>
  )
}

export default CreateStructuralElement
